/**
 * CA / Super Admin Practice Management Service
 * apps/gst-portal/src/services/practiceManagementService.ts
 */

import { ClientPortfolioSummary } from '../types/gst';

export interface StatutoryDeadline {
  form: string;
  targetCategory: string;
  dayOfMonth: number;
  description: string;
  dueDate: string;
  daysRemaining: number;
  status: 'UPCOMING' | 'DUE_SOON' | 'OVERDUE';
}

/**
 * Calculates current month statutory GST deadlines
 */
export function calculateStatutoryDeadlines(currentDate: Date = new Date()): StatutoryDeadline[] {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth(); // 0-indexed

  const formatDue = (day: number) => {
    const d = new Date(year, month, day);
    return d.toISOString().split('T')[0];
  };

  const getDaysDiff = (day: number) => {
    const due = new Date(year, month, day, 23, 59, 59);
    const diffTime = due.getTime() - currentDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const forms = [
    {
      form: 'GSTR-1',
      targetCategory: 'Monthly Filers (Turnover > ₹5 Cr or Non-QRMP)',
      dayOfMonth: 11,
      description: 'Monthly statement of outward supplies of goods or services'
    },
    {
      form: 'IFF (Invoice Furnishing Facility)',
      targetCategory: 'QRMP Quarterly Filers (B2B invoices only)',
      dayOfMonth: 13,
      description: 'Optional B2B invoice upload for Month 1 & 2 under QRMP scheme'
    },
    {
      form: 'GSTR-3B',
      targetCategory: 'Monthly Regular Filers',
      dayOfMonth: 20,
      description: 'Monthly summary return with tax liability payment and ITC offset'
    },
    {
      form: 'GSTR-3B (QRMP Group 1)',
      targetCategory: 'Quarterly Filers (Maharashtra, Gujarat, Karnataka, TN, etc.)',
      dayOfMonth: 22,
      description: 'Quarterly return filing for Category A States'
    },
    {
      form: 'GSTR-3B (QRMP Group 2)',
      targetCategory: 'Quarterly Filers (Delhi, UP, Haryana, Punjab, Rajasthan, etc.)',
      dayOfMonth: 24,
      description: 'Quarterly return filing for Category B States'
    }
  ];

  return forms.map(f => {
    const days = getDaysDiff(f.dayOfMonth);
    let status: StatutoryDeadline['status'] = 'UPCOMING';
    if (days < 0) {
      status = 'OVERDUE';
    } else if (days <= 3) {
      status = 'DUE_SOON';
    }

    return {
      form: f.form,
      targetCategory: f.targetCategory,
      dayOfMonth: f.dayOfMonth,
      description: f.description,
      dueDate: formatDue(f.dayOfMonth),
      daysRemaining: days,
      status
    };
  });
}

/**
 * Calculates Section 47 Late Fee
 * Standard: ₹50 per day (₹25 CGST + ₹25 SGST)
 * Nil return: ₹20 per day (₹10 CGST + ₹10 SGST)
 * Maximum statutory cap: ₹500 for Nil return, ₹1,000 to ₹5,000 depending on turnover.
 */
export function calculateSection47LateFee(
  daysLate: number,
  isNilReturn: boolean = false,
  annualTurnoverLakhs: number = 100
): { dailyRate: number; totalLateFee: number; capApplied: boolean } {
  if (daysLate <= 0) return { dailyRate: 0, totalLateFee: 0, capApplied: false };

  const dailyRate = isNilReturn ? 20 : 50;
  const rawFee = daysLate * dailyRate;

  let maxCap = 5000;
  if (isNilReturn) {
    maxCap = 500;
  } else if (annualTurnoverLakhs <= 150) {
    maxCap = 2000; // ₹1,000 CGST + ₹1,000 SGST
  } else if (annualTurnoverLakhs <= 500) {
    maxCap = 5000; // ₹2,500 CGST + ₹2,500 SGST
  }

  const totalLateFee = Math.min(rawFee, maxCap);
  return {
    dailyRate,
    totalLateFee,
    capApplied: rawFee > maxCap
  };
}

/**
 * Calculates Section 50 Interest (18% p.a. on unpaid net cash tax liability)
 */
export function calculateSection50Interest(
  netCashTaxDue: number,
  daysLate: number
): number {
  if (daysLate <= 0 || netCashTaxDue <= 0) return 0;
  // Interest = Principal * (18 / 100) * (days / 365)
  const interest = (netCashTaxDue * 0.18 * daysLate) / 365;
  return Math.round(interest * 100) / 100;
}

/**
 * Mock Multi-Client Portfolio for CA Practice Management
 */
export const SAMPLE_CLIENT_PORTFOLIO: ClientPortfolioSummary[] = [
  {
    clientId: 'client_verve_technologies',
    legalName: 'Verve Technologies Pvt Ltd',
    tradeName: 'Verve AI Solutions',
    gstin: '27AABCU9603R1ZM',
    stateName: 'Maharashtra',
    contactEmail: 'accounts@verveailabs.com',
    contactPhone: '+91 98200 11223',
    filingFrequency: 'MONTHLY',
    entitlements: {
      enableReturns: true,
      enableBilling: true,
      enablePracticeMgmt: true
    },
    gstr1Status: 'FILED',
    gstr1Arn: 'AA270725001234F',
    gstr3bStatus: 'READY',
    gstr2bReconStatus: 'COMPLETED',
    unreconciledCount: 0,
    taxAtRisk: 0,
    estimatedLateFeeSec47: 0,
    estimatedInterestSec50: 0,
    daysUntilDeadline: 8
  },
  {
    clientId: 'client_apex_logistics',
    legalName: 'Apex Multimodal Logistics LLP',
    tradeName: 'Apex Express',
    gstin: '27AAKFA4589K1Z5',
    stateName: 'Maharashtra',
    contactEmail: 'tax@apexlogistics.in',
    contactPhone: '+91 98111 88442',
    filingFrequency: 'MONTHLY',
    entitlements: {
      enableReturns: true,
      enableBilling: false,
      enablePracticeMgmt: false
    },
    gstr1Status: 'READY',
    gstr3bStatus: 'PENDING',
    gstr2bReconStatus: 'IN_PROGRESS',
    unreconciledCount: 4,
    taxAtRisk: 48500,
    estimatedLateFeeSec47: 0,
    estimatedInterestSec50: 0,
    daysUntilDeadline: 8
  },
  {
    clientId: 'client_zenith_retail',
    legalName: 'Zenith Retail Stores India Ltd',
    tradeName: 'Zenith Mart',
    gstin: '29AABCZ8890M1ZE',
    stateName: 'Karnataka',
    contactEmail: 'finance@zenithmart.co',
    contactPhone: '+91 99880 77665',
    filingFrequency: 'MONTHLY',
    entitlements: {
      enableReturns: false,
      enableBilling: true,
      enablePracticeMgmt: false
    },
    gstr1Status: 'PENDING',
    gstr3bStatus: 'PENDING',
    gstr2bReconStatus: 'NOT_STARTED',
    unreconciledCount: 12,
    taxAtRisk: 142000,
    estimatedLateFeeSec47: 150,
    estimatedInterestSec50: 420.5,
    daysUntilDeadline: -1
  },
  {
    clientId: 'client_cloudscale_systems',
    legalName: 'CloudScale Systems Pvt Ltd',
    tradeName: 'CloudScale AI',
    gstin: '07AAACC2211C1ZX',
    stateName: 'Delhi',
    contactEmail: 'billing@cloudscale.ai',
    contactPhone: '+91 98710 44332',
    filingFrequency: 'QRMP',
    entitlements: {
      enableReturns: true,
      enableBilling: true,
      enablePracticeMgmt: false
    },
    gstr1Status: 'FILED',
    gstr1Arn: 'AA070725008912P',
    gstr3bStatus: 'PENDING',
    gstr2bReconStatus: 'COMPLETED',
    unreconciledCount: 0,
    taxAtRisk: 0,
    estimatedLateFeeSec47: 0,
    estimatedInterestSec50: 0,
    daysUntilDeadline: 12
  }
];
